// stores/panelDataStore.older.ts — left-edge infinite-scroll backfill orchestration (BUILD #1)
//
// Extracted from panelDataStore.ts so that file stays < 250 LOC. Owns the older-history
// fetch state machine (single-flight + window widening + exhaustion latch) and the transient
// abort-handle map. The store's `loadOlder` action is a thin delegate to `runLoadOlder`.
//
// Contract: fetch a window of 1m candles strictly older than the current oldest candle and
// prepend them (store.prependCandles → dedupeSorted keeps timestamps strictly ascending, so
// applyCandles never asserts "Cannot update oldest data"). The window doubles on each
// short/empty response (200 → 400 → 800 → 1600 minutes) to punch past sparse/synthetic-gap
// regions, latching `_noMoreHistory` after OLDER_MAX_ATTEMPTS — matching the legacy
// ChartContainer.handleNearLeftEdge exhaustion logic.

import { loadOlderCandles, type SymbolLoadHandle } from '../api/panelDataFetcher';
import { SERVER_MAX_LIMIT } from '../api/candleDepth';
import { omitKey } from './panelDataMutations';
import type { PanelDataState, SymbolKey } from './panelDataStore.types';

/** Transient abort handles for in-flight OLDER-history loads, kept out of store state
 *  (side-effect bookkeeping only). Aborted in the store's clear(symbol). */
export const inflightOlder = new Map<SymbolKey, SymbolLoadHandle>();

/** Max widen steps before latching `_noMoreHistory`; the window doubles each step. */
const OLDER_MAX_ATTEMPTS = 3;

/** DISPLAYED bars to gain per left-edge backfill, at the panel's own timeframe. */
const OLDER_BATCH_DISPLAY_BARS = 300;

/**
 * Time SPAN (ms) a backfill should reach back from the current oldest candle, so it yields
 * ~OLDER_BATCH_DISPLAY_BARS candles of the panel's timeframe. Doubles per widen attempt.
 *
 * The span — not a row count — is what must scale with the timeframe. The store is
 * 1m-base-only (higher TFs derive via aggregateCandles), and /api/candles serves at most
 * SERVER_MAX_LIMIT (10000) 1m rows per request. 10000 minutes is under 7 days, i.e. ~7
 * candles at 1d, so ANY row-count-based window is hopeless at high TFs: the old flat
 * 1500-row batch added ~6 visible bars at 4h and ~1 at 1d, and the chart looked frozen.
 */
export function olderSpanMsFor(interval: number, widen: number): number {
  const tf = interval > 0 ? interval : 1;
  return OLDER_BATCH_DISPLAY_BARS * tf * 60_000 * Math.pow(2, widen);
}

/**
 * How many sequential requests one backfill may chain. A single /api/candles call returns at
 * most SERVER_MAX_LIMIT contiguous 1m rows (~7 days), which is only ~7 bars at 1d — so a
 * high-TF scroll must chain calls to gain a visible chunk. Each hop walks `to` further back,
 * keeping the series contiguous (no synthetic-gap holes).
 */
const MAX_CHAIN_HOPS = 8;

/** 1m rows one request can return contiguously. */
const HOP_SPAN_MS = SERVER_MAX_LIMIT * 60_000;

/** Requests needed to cover `spanMs`, bounded by MAX_CHAIN_HOPS. */
export function hopsFor(spanMs: number): number {
  return Math.max(1, Math.min(MAX_CHAIN_HOPS, Math.ceil(spanMs / HOP_SPAN_MS)));
}

type Get = () => PanelDataState;
type Set = (
  partial:
    | Partial<PanelDataState>
    | ((s: PanelDataState) => Partial<PanelDataState>),
) => void;

/** Widen the next window, or latch `_noMoreHistory` once the attempts are exhausted. */
function widenOrLatch(set: Set, symbol: SymbolKey, widen: number): void {
  if (widen >= OLDER_MAX_ATTEMPTS) {
    set((st) => ({ _noMoreHistory: { ...st._noMoreHistory, [symbol]: true } }));
  } else {
    set((st) => ({ _olderAttempts: { ...st._olderAttempts, [symbol]: widen + 1 } }));
  }
}

/**
 * Single-flighted older-history backfill for one symbol. Re-entrant calls no-op while a fetch
 * is in flight or history is exhausted; the entry must be 'ready' with at least one candle.
 */
export function runLoadOlder(
  get: Get,
  set: Set,
  symbol: SymbolKey,
  interval = 1,
): void {
  const s = get();
  if (s._loadingOlder[symbol] || s._noMoreHistory[symbol]) return;
  const entry = s.dataByKey[symbol];
  if (!entry || entry.loadState !== 'ready' || entry.candles.length === 0) return;
  const oldest = entry.candles[0]!.startTs;
  const lenBefore = entry.candles.length;
  const widen = s._olderAttempts[symbol] ?? 0;
  const spanMs = olderSpanMsFor(interval, widen);
  const hops = hopsFor(spanMs);
  set((st) => ({ _loadingOlder: { ...st._loadingOlder, [symbol]: true } }));

  // Chain up to `hops` sequential requests, each walking `to` back to the oldest row the
  // previous one returned. One request tops out at ~7 days of 1m rows, so a 1d panel needs
  // several to gain a screenful; doing them here (rather than one-per-scroll-event) is what
  // makes a single left-edge trigger produce a visible jump at 4h/1d.
  void (async (): Promise<void> => {
    try {
      let cursor = oldest;
      for (let hop = 0; hop < hops; hop++) {
        if ((get().refcounts[symbol] ?? 0) === 0) return; // symbol dropped mid-flight
        const handle = loadOlderCandles(symbol, cursor, spanMs);
        inflightOlder.set(symbol, handle);
        const res = await handle.promise;
        if ((get().refcounts[symbol] ?? 0) === 0) return;
        if (res.candles.length === 0) break; // ran out of history — settle below
        get().prependCandles(symbol, res.candles);
        const nextCursor = res.candles[0]!.startTs;
        if (nextCursor >= cursor) break; // no backward progress; avoid a spin
        cursor = nextCursor;
      }

      const lenAfter = get().dataByKey[symbol]?.candles.length ?? lenBefore;
      if (lenAfter === lenBefore) {
        // Nothing new across the whole chain — widen the next window, or latch exhaustion.
        widenOrLatch(set, symbol, widen);
      } else {
        // Gained new history — reset the widen counter for the next scroll.
        set((st) => ({ _olderAttempts: omitKey(st._olderAttempts, symbol) }));
      }
    } catch {
      // LoadAbortedError (symbol torn down) or a transient REST failure — don't latch
      // `_noMoreHistory`; the next scroll re-tries. Just release the single-flight guard.
    } finally {
      inflightOlder.delete(symbol);
      set((st) => ({ _loadingOlder: omitKey(st._loadingOlder, symbol) }));
    }
  })();
}
